<template>
  <v-form ref="form" v-model="valid">
    <div>
      <v-alert
        v-if="modalIssue"
        :closable="false"
        color="red-faint"
        icon="icon-warning-xs"
        density="compact"
        class="mb-4 text-red-dark"
      >
        <div class="body2 text-red-dark" v-html="modalIssue"></div>
      </v-alert>

      <v-alert
        v-if="teamsError.length"
        :closable="false"
        color="red-faint"
        icon="icon-warning-xs"
        density="compact"
        class="mb-4 text-red-dark"
      >
        <div class="subtitle1 text-red-dark">{{ $t("modals.psaTicketsMap.teamsErrorTitle") }}</div>
        <div class="body2 text-red-dark">&bull; {{ getConcatenatedBoards(teamsError) }}</div>
        <div class="subtitle2 text-red-dark">
          {{ $t("modals.psaTicketsMap.teamsErrorSubtitle") }}
        </div>
      </v-alert>
      <div>
        <span class="subtitle1">{{ $t("modals.psaTicketsMap.workspace") }}</span>
        <span class="ml-2">{{ config.item.workspaceName }}</span>
      </div>
      <div>
        <span class="subtitle1">{{ $t("modals.psaTicketsMap.company") }}</span>
        <span class="ml-2">{{ config.item.companyName }}</span>
      </div>

      <v-divider class="mt-6 mb-6"></v-divider>
      <div class="subtitle2">{{ $t("modals.psaTicketsMap.tableTitle") }}</div>
      <v-btn-toggle
        v-model="localValue.isAll"
        mandatory
        borderless
        density="compact"
        variant="flat"
        color="white"
        rounded
        class="mt-4 mb-4 pa-1 bg-indigo-faint ga-1"
        base-color="indigo-faint"
        selected-class="bg-white opacity-100"
      >
        <v-btn
          class="text-primary flex-grow-1 frequency-button subtitle2 w-0 switch-button-group-btn"
          :value="true"
          :key="'valueTrue'"
          :ripple="false"
          :class="{
            'switch-button-group-btn--active': localValue.isAll === true,
          }"
        >
          {{ $t(`modals.psaTicketsMap.mapAllModules`) }}
        </v-btn>
        <v-btn
          class="text-primary flex-grow-1 frequency-button subtitle2 w-0 switch-button-group-btn"
          :value="false"
          :key="'valueFalse'"
          :ripple="false"
          :class="{
            'switch-button-group-btn--active': localValue.isAll === false,
          }"
        >
          {{ $t(`modals.psaTicketsMap.mapIndividualModules`) }}
        </v-btn>
      </v-btn-toggle>
      <v-progress-linear
        :style="{ visibility: loadingIndicators.tableLoading ? 'visible' : 'hidden' }"
        indeterminate
        height="2px"
      />
      <v-table>
        <template #default>
          <thead>
            <tr>
              <th class="w-20">{{ $t("modals.psaTicketsMap.module") }}</th>
              <th class="w-30 text-right">
                {{ $t("modals.psaTicketsMap.cwServiceBoard") }}
              </th>
              <th class="w-25 text-right">
                {{ $t("modals.psaTicketsMap.openTicketStatus") }}
              </th>
              <th class="w-25 text-right">
                {{ $t("modals.psaTicketsMap.closeTicketStatus") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(mappingRow, index) in localValue.mapping" :key="index">
              <td class="body2">
                {{
                  localValue.isAll
                    ? $t("modals.psaTicketsMap.allModules")
                    : $t(`subscriptions.modules.${mappingRow.module}`)
                }}
                <v-tooltip
                  v-if="localValue.isAll && subscribedModules.length"
                  open-delay="300"
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-icon v-bind="props" icon="$info" />
                  </template>
                  <div>
                    <div class="text-indigo-light">
                      {{ $t("modals.psaTicketsMap.subscribedModules") }}
                    </div>
                    <div v-for="(subscription, index) in subscribedModules" :key="index">
                      <div v-for="module in subscription.modules" :key="module">
                        {{ $t(`subscriptions.modules.${module}`) }}
                      </div>
                    </div>
                  </div>
                </v-tooltip>
              </td>
              <td align="right">
                <v-select
                  v-if="shouldShowBoardsSelect(mappingRow)"
                  :placeholder="$t('connectors.psa.mappingSelectPlaceholder')"
                  :model-value="mappingRow.serviceBoard"
                  @update:model-value="(value) => onServiceBoardSelect(value, index)"
                  class="mapping-select"
                  :items="boards"
                  item-title="name"
                  item-value="id"
                  :rules="[serviceBoardValidationRule]"
                  return-object
                  :menu-props="{ contentClass: 'mapped-items-select' }"
                  hide-details
                  variant="plain"
                >
                  <template v-slot:item="{ item, props }">
                    <v-list-item
                      :key="item.value"
                      v-bind="props"
                      :title="''"
                      @click="mappingUpdate(item.raw, index, 'serviceBoard')"
                    >
                      {{ item.raw.name }}
                    </v-list-item>
                  </template>
                </v-select>
                <span v-else-if="loadingIndicators.boardsLoaded" class="text-red-dark">
                  {{ $t("modals.psaTicketsMap.noBoardInputMessage") }}
                </span>
              </td>
              <td align="right">
                <div v-if="shouldShowBoardsSelect(mappingRow)">
                  <v-select
                    :placeholder="$t('connectors.psa.mappingSelectPlaceholder')"
                    :model-value="mappingRow.openTicketStatus"
                    class="mapping-select"
                    v-if="shouldShowTicketsStatusesSelect(mappingRow, 'openTicketStatus')"
                    :items="getTicketStatusesForBoard(mappingRow.serviceBoard?.id)"
                    item-title="name"
                    item-value="id"
                    :rules="[mappingRowValidationRule(mappingRow)]"
                    return-object
                    :loading="
                      loadingIndicators.ticketsLoading.includes(mappingRow.serviceBoard?.id || '')
                    "
                    :menu-props="{ contentClass: 'mapped-items-select' }"
                    hide-details
                    variant="plain"
                  >
                    <template v-slot:item="{ item, props }">
                      <v-list-item
                        :key="item.value"
                        v-bind="props"
                        @click="mappingUpdate(item.raw, index, 'openTicketStatus')"
                      >
                      </v-list-item>
                    </template>
                  </v-select>
                  <span
                    v-else-if="
                      loadingIndicators.ticketsLoaded.includes(mappingRow.serviceBoard?.id || '')
                    "
                    class="text-red-dark d-flex align-center justify-end"
                    >{{ $t("modals.psaTicketsMap.noTicketStatusInputMessage") }}
                    <v-tooltip open-delay="300" location="top">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="$info" class="text-red-dark" />
                      </template>
                      <div>
                        {{ $t("modals.psaTicketsMap.noTicketStatusTooltip") }}
                      </div>
                    </v-tooltip>
                  </span>
                </div>
              </td>
              <td align="right">
                <div v-if="shouldShowBoardsSelect(mappingRow)">
                  <v-select
                    :placeholder="$t('connectors.psa.mappingSelectPlaceholder')"
                    :model-value="mappingRow.closedTicketStatus"
                    class="mapping-select"
                    v-if="shouldShowTicketsStatusesSelect(mappingRow, 'closedTicketStatus')"
                    :items="getTicketStatusesForBoard(mappingRow.serviceBoard?.id)"
                    item-title="name"
                    item-value="id"
                    :rules="[mappingRowValidationRule(mappingRow)]"
                    return-object
                    :loading="
                      loadingIndicators.ticketsLoading.includes(mappingRow.serviceBoard?.id || '')
                    "
                    :menu-props="{ contentClass: 'mapped-items-select' }"
                    hide-details
                    variant="plain"
                  >
                    <template v-slot:item="{ item, props }">
                      <v-list-item
                        :key="item.value"
                        v-bind="props"
                        @click="mappingUpdate(item.raw, index, 'closedTicketStatus')"
                      >
                      </v-list-item>
                    </template>
                  </v-select>

                  <span
                    v-else-if="
                      mappingRow.serviceBoard &&
                      loadingIndicators.ticketsLoaded.includes(mappingRow.serviceBoard?.id)
                    "
                    class="text-red-dark d-flex align-center justify-end"
                    >{{ $t("modals.psaTicketsMap.noTicketStatusInputMessage") }}
                    <v-tooltip open-delay="300" location="top">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" icon="$info" class="text-red-dark" />
                      </template>
                      <div>
                        {{ $t("modals.psaTicketsMap.noTicketStatusTooltip") }}
                      </div>
                    </v-tooltip>
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>
  </v-form>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  type PropType,
  type Ref,
  ref,
  watch,
} from "vue";
import {
  type ConnectwiseTicketMapping,
  type ConnectwiseTicketMappingEntity,
  type PsaTicketMappingApiError,
  PsaTicketMappingErrorType,
  type PsaWorkspaceSubscriptions,
  usePsaConfigurationStore,
} from "@/_store/connectors/psa-configuration.module.ts";
import { PsaProviders } from "@/constants/connectors.ts";
import { useI18n } from "vue-i18n";
import type { VuetifyFormRef } from "@/types.ts";
import type { SubscriptionModule } from "@/constants/workplaces.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { PsaTicketMappingForm, PsaTicketMappingItem } from "@/_store/connectors/adapters.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

interface MappingObject {
  module: SubscriptionModule | "";
  serviceBoard: ConnectwiseTicketMappingEntity | null;
  openTicketStatus: ConnectwiseTicketMappingEntity | null;
  closedTicketStatus: ConnectwiseTicketMappingEntity | null;
}

type MappingFieldKey = "serviceBoard" | "openTicketStatus" | "closedTicketStatus";

const isPsaTicketMappingApiError = (error: unknown): error is PsaTicketMappingApiError => {
  if (!error || typeof error !== "object") {
    return false;
  }

  return "errorType" in error && "errorMessages" in error;
};

const DEFAULT_MAPPING: PsaTicketMappingItem[] = [
  {
    module: "",
    serviceBoard: null,
    openTicketStatus: null,
    closedTicketStatus: null,
  },
];

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<ConnectwiseTicketMapping>>,
      required: true,
    },
  },
  emits: ["dismissed", "update:valid", "change-footer", "update:localValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const psaConfigurationStore = usePsaConfigurationStore(PsaProviders.CONNECTWISE);
    const valid = ref(false);
    const formSubmitted = ref(false);
    const isInitializing = ref(true);
    const form = ref<VuetifyFormRef>();
    const workspacesSubscriptions: Ref<PsaWorkspaceSubscriptions[]> = ref([]);
    const localValue: Ref<PsaTicketMappingForm> = ref({
      isAll: !props.config.item.modulesMapping.length,
      mapping: DEFAULT_MAPPING,
    });

    const loadingIndicators = ref({
      tableLoading: false,
      ticketsLoading: [] as string[],
      boardsLoaded: false,
      ticketsLoaded: [] as string[],
    });
    const boards: Ref<ConnectwiseTicketMappingEntity[]> = ref([]);
    const ticketStatuses: Ref<Record<string, ConnectwiseTicketMappingEntity[]>> = ref({});
    const errorMessages: Ref<string[]> = ref([]);
    const teamsError: Ref<ConnectwiseTicketMappingEntity[]> = ref([]);

    const actionNotAllowed = computed(() =>
      isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.WORKSPACE_MANAGEMENT,
        WorkspaceManagementScopeSections.CONNECTORS
      )
    );

    const saveConfig = async () => {
      formSubmitted.value = true;
      const { errors } = await form.value!.validate();

      errorMessages.value = errors.flatMap((error) => error.errorMessages);

      if (errorMessages.value.length) {
        return;
      }

      try {
        await psaConfigurationStore.updateTicketMapping(
          localValue.value,
          props.config.item.workspaceId
        );
        emit("dismissed");
      } catch (error: unknown) {
        if (
          isPsaTicketMappingApiError(error) &&
          error.errorType === PsaTicketMappingErrorType.TEAMS
        ) {
          teamsError.value = error.errorMessages;
        }
      }
    };

    const initializeMappings = () => {
      const editedMapping = props.config.item;
      let modulesMapping: MappingObject[] = [];

      if (!localValue.value.isAll) {
        modulesMapping = workspacesSubscriptions.value[0].modules.map((module) => {
          const moduleMappingObj = editedMapping.modulesMapping.find(
            (m) => m.moduleName === module
          );
          if (moduleMappingObj) {
            return {
              module: moduleMappingObj.moduleName,
              serviceBoard: moduleMappingObj.serviceBoard ?? null,
              openTicketStatus: moduleMappingObj.openTicketStatus ?? null,
              closedTicketStatus: moduleMappingObj.closedTicketStatus ?? null,
            };
          }

          return {
            module,
            serviceBoard: null,
            openTicketStatus: null,
            closedTicketStatus: null,
          };
        });
      }

      localValue.value = {
        ...localValue.value,
        mapping: !localValue.value.isAll
          ? modulesMapping
          : [
              {
                module: "",
                serviceBoard: editedMapping.serviceBoard,
                openTicketStatus: editedMapping.openTicketStatus,
                closedTicketStatus: editedMapping.closedTicketStatus,
              },
            ],
      };
    };

    const initializeBoards = async () => {
      for (let i = 0; i < localValue.value.mapping.length; i++) {
        const serviceBoard = localValue.value.mapping[i].serviceBoard;

        if (serviceBoard && !ticketStatuses.value[serviceBoard.id]) {
          onServiceBoardSelect(serviceBoard);
        }
      }

      loadingIndicators.value.tableLoading = true;
      boards.value = await psaConfigurationStore.getConnectwiseBoards(
        props.config.item.workspaceId
      );

      loadingIndicators.value.boardsLoaded = true;
      loadingIndicators.value.tableLoading = false;
    };

    const updateFooter = () => {
      const footer = {
        buttons: [
          {
            title: i18n.t("general.cancel"),
            spacer: "before",
            type: "text",
            cancel: true,
          },
          {
            title: i18n.t("modals.psaTicketsMap.actionBtn"),
            color: "primary",
            callback: saveConfig,
          },
        ],
      };

      emit("change-footer", footer);
    };

    const initializeModal = async () => {
      workspacesSubscriptions.value = await psaConfigurationStore.getSubscribedModules([
        props.config.item.workspaceId,
      ]);

      initializeMappings();
      await initializeBoards();

      emit("update:localValue", localValue.value);

      updateFooter();
      await nextTick();
      isInitializing.value = false;
    };

    onMounted(async () => {
      await initializeModal();
    });

    const shouldShowBoardsSelect = (item: MappingObject) => {
      return (
        (loadingIndicators.value.boardsLoaded && boards.value.length) ||
        !loadingIndicators.value.boardsLoaded ||
        item.serviceBoard
      );
    };

    watch(
      () => localValue.value.isAll,
      () => {
        if (isInitializing.value) {
          return;
        }

        initializeMappings();
      }
    );

    const onServiceBoardSelect = async (
      value: ConnectwiseTicketMappingEntity,
      index: number = -1
    ) => {
      loadingIndicators.value.ticketsLoading.push(value.id);

      if (index >= 0) {
        localValue.value.mapping[index].openTicketStatus = null;
        localValue.value.mapping[index].closedTicketStatus = null;
      }

      const result = await psaConfigurationStore.getConnectwiseTicketStatuses(value.id);

      ticketStatuses.value = {
        ...ticketStatuses.value,
        [value.id]: result,
      };
      loadingIndicators.value.ticketsLoaded.push(value.id);

      loadingIndicators.value.ticketsLoading = loadingIndicators.value.ticketsLoading.filter(
        (item) => item !== value.id
      );
    };

    const mappingUpdate = (
      selectedItem: ConnectwiseTicketMappingEntity,
      updateByIndex: number,
      fieldName: MappingFieldKey
    ) => {
      localValue.value.mapping[updateByIndex][fieldName] = selectedItem;
    };

    const getTicketStatusesForBoard = (boardId?: string) => {
      if (boardId) {
        return ticketStatuses.value[boardId];
      }

      return [];
    };

    const modalIssue = computed(() => {
      const noBoards = loadingIndicators.value.boardsLoaded && !boards.value.length;
      if (noBoards) {
        return i18n.t("modals.psaTicketsMap.noBoardInputMessage");
      }

      if (formSubmitted.value && errorMessages.value.length) {
        return i18n.t("modals.psaTicketsMap.requiredValueEmpty");
      }

      return "";
    });

    const mappingRowValidationRule =
      (mappingRow: MappingObject) => (val: ConnectwiseTicketMappingEntity) => {
        if (!val && !!mappingRow.serviceBoard) {
          return "required";
        }

        return true;
      };

    const serviceBoardValidationRule = (val: ConnectwiseTicketMappingEntity | null) => {
      if (!val) {
        return true;
      }
      const boardHasIssues = teamsError.value.some((item) => item.id === val.id);

      if (boardHasIssues) {
        return "error";
      }

      return true;
    };

    const shouldShowTicketsStatusesSelect = (item: MappingObject, key: MappingFieldKey) => {
      const serviceBoardId = item.serviceBoard?.id || "";
      const ticketsAreNotLoaded = !loadingIndicators.value.ticketsLoaded.includes(serviceBoardId);
      const ticketStatusesIsNotEmpty = !!ticketStatuses.value[serviceBoardId]?.length;

      return ticketsAreNotLoaded || item[key] || ticketStatusesIsNotEmpty;
    };

    const getConcatenatedBoards = (boardsList: ConnectwiseTicketMappingEntity[]): string => {
      return boardsList.map((item) => item.name).join(", ");
    };

    return {
      loadingIndicators,
      mappingRowValidationRule,
      actionNotAllowed,
      localValue,
      valid,
      form,
      modalIssue,
      mappingUpdate,
      subscribedModules: workspacesSubscriptions,
      boards,
      onServiceBoardSelect,
      ticketStatuses,
      getTicketStatusesForBoard,
      shouldShowBoardsSelect,
      shouldShowTicketsStatusesSelect,
      teamsError,
      getConcatenatedBoards,
      serviceBoardValidationRule,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  box-shadow: none !important;
  border: 1px solid rgb(var(--v-theme-indigo-pale));
}

:deep(.switch-button-group-btn) {
  .v-btn__overlay {
    opacity: 0 !important;
  }
}

.v-btn.v-btn--active {
  background-color: rgb(var(--v-theme-white)) !important;
  font-weight: 600;
}

.v-btn-group {
  width: 400px;
}

.v-btn-group > .v-btn {
  text-transform: capitalize !important;
}

.frequency-button {
  font-weight: normal;
  letter-spacing: normal;
}

.text-align-right {
  text-align: right;
  text-align: -webkit-right;
}

:deep(*) {
  .text-red-dark::before {
    color: rgb(var(--v-theme-red-dark));
  }

  .mapping-select {
    width: fit-content;

    .v-field__input {
      min-width: 30px;
      margin-right: 10px;
      padding: 10px 0 !important;
      opacity: 1;

      input::placeholder {
        color: rgb(var(--v-theme-link)) !important;
      }

      .v-select__selection-text {
        color: rgb(var(--v-theme-primary));
      }
    }

    .v-field--error {
      input::placeholder,
      .v-select__selection-text {
        color: rgb(var(--v-theme-red-dark)) !important;
      }
    }
  }
}
</style>
